// frontend/src/components/Cart/utils.ts
import { PaymentIntent } from "@stripe/stripe-js/types/api/payment-intents";
import { notEmpty } from "@uplift-ltd/ts-helpers";
import { useMemo } from "react";
import { ProductTypesEnum } from "__generated__/globalTypes";
import {
  CartInfoNode as Product,
  CartInfoNodes as CartItem,
  CartQueryCart as Cart,
} from "components/Cart/__generated__/queries";
import { splitCoursesAndBundles } from "components/courses/MyCourses/util";
import {
  GTM_CONVERSION_EVENT_ADD_TO_CART,
  GTM_CONVERSION_EVENT_PURCHASE,
  KLAVIYO_PUBLIC_API_KEY,
} from "constants/site";

// Extend Window interface to include dataLayer only
declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

export type StripeStatus = PaymentIntent.Status | false | null;

export const getStripeClientSecret = (): string | null =>
  new URLSearchParams(window.location.search).get("payment_intent_client_secret");

/**
 * Push an "add_to_cart" event into dataLayer for GTM.
 */
export const triggerAddToCartConversionTrackers = (): void => {
  if (GTM_CONVERSION_EVENT_ADD_TO_CART) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "add_to_cart",
      send_to: GTM_CONVERSION_EVENT_ADD_TO_CART,
    });
  }
};

/**
 * Push a "purchase" event into dataLayer for GTM and track in Klaviyo.
 */
export const triggerPurchaseConversionTrackers = (
  amount: number,
  currency: string,
  transactionId: string | number,
  productIds: number[],
  productNames?: string[],
  itemPrices?: number[]
): void => {
  if (GTM_CONVERSION_EVENT_PURCHASE) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "purchase",
      send_to: GTM_CONVERSION_EVENT_PURCHASE,
      ecommerce: {
        transaction_id: transactionId,
        value: amount,
        currency: currency.toUpperCase(),
        items: productIds.map((productId, index) => ({
          item_id: productId.toString(),
          item_name: productNames?.[index]?.trim() || productId.toString(),
          price: itemPrices?.[index] || amount / productIds.length, // Use actual price or distribute total
          quantity: 1,
        })),
      },
    });
  }

  // Klaviyo
  if (KLAVIYO_PUBLIC_API_KEY && window.klaviyo) {
    window.klaviyo.push([
      "track",
      "Purchase",
      {
        ProductIds: productIds,
        Value: amount,
        Currency: currency.toUpperCase(),
        TransactionId: transactionId,
      },
    ]);
  }
};

/**
 * Hook to determine if a product is bundled based on cart items.
 */
export const useIsProductBundled = (items: CartItem[]) => {
  const { individualCourses } = useMemo(
    () => ({
      individualCourses: splitCoursesAndBundles(items.map(n => n?.product?.node).filter(notEmpty))
        .individualCourses,
    }),
    [items]
  );

  return (product: Product) =>
    product.type === ProductTypesEnum.SIMPLE && !individualCourses.includes(product);
};

/**
 * Hook to extract product IDs from the cart.
 */
export const useProductIds = (cart: Cart | null | undefined): number[] =>
  useMemo(
    () =>
      cart?.contents?.nodes
        ?.map(node => parseInt(node?.product?.node?.id || "", 10))
        .filter(notEmpty) || [],
    [cart]
  );
